import { Box, Divider, RadioGroup, styled, Typography } from '@mui/material';
import React, { useContext } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../../../components/Button/Button';
import ButtonGroup from '../../../../../components/ButtonGroup/ButtonGroup';
import Checkbox from '../../../../../components/Checkbox/Checkbox';
import Radio from '../../../../../components/Radio/Radio';
import SectionTitle from '../../../../../components/Text/TextStyles';
import WarningIconTransparent from '../../../../../components/Icons/WarningIconTransparent';
import UserDetailsContext from '../../../../../contexts/UserDetailsContext/UserDetailsContext';
import { NotificationSetting, NotificationType } from '../../../../../models/CompanyOnboardingModels';
import { RootState } from '../../../../../stores/Store';
import { updateRequest } from '../../../../../stores/UpgradeDeviceStore';

const RowsContainer = styled(Box)(() => ({
  paddingBottom: '2rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
}));

const Row = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
}));

const SectionValue = styled(Typography)(() => ({
  lineHeight: 'normal',
  marginLeft: 'auto',
  fontWeight: '700',
  width: '50%',
  textAlign: 'right',
}));

const SectionValueDisabled = styled(Typography)(({ theme }) => ({
  lineHeight: 'normal',
  marginLeft: 'auto',
  fontWeight: '700',
  color: theme.palette.text.disabled,
  width: '50%',
  textAlign: 'right',
}));

const SectionLabel = styled(Typography)(() => ({
  lineHeight: 'normal',
  width: '50%',
}));

const SectionLabelDisabled = styled(Typography)(({ theme }) => ({
  lineHeight: 'normal',
  width: '50%',
  color: theme.palette.text.disabled,
}));

const SectionTitleBlack = styled(Typography)(() => ({
  fontSize: '1.375rem',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  paddingBottom: '1.5rem',
}));

const WarningContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.container.background,
  marginTop: '1rem',
  marginBottom: '1.5rem',
  borderRadius: '0.375rem',
  border: `1px solid ${theme.palette.warning.main}`,
}));

interface UpgradeDeviceSummaryStepProps {
  nextStep: () => void;
  onClose: () => void;
  isLoading: boolean;
}

function UpgradeDeviceSummaryStep({ nextStep, onClose, isLoading }: UpgradeDeviceSummaryStepProps) {
  const [t] = useTranslation();
  const {
    newDevice,
    deviceToUpgrade,
    upgradeDeviceRequest,
    selectedSimId,
    selectedSimType,
    qrCodeNotificationSettings,
    hasDistributionList,
    transferEligible,
    transferPullEnabled,
  } = useSelector((state: RootState) => state.upgradeDeviceReducer);
  const dispatch = useDispatch();
  const user = useContext(UserDetailsContext);

  /**
   * Handles change of the transfer radio button
   * @param e
   */
  const handleTransferRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateRequest({ transfer: e.target.value === 'true' }));
  };

  /**
   * Handles change of the notification radio button
   * @param e
   */
  const handleNotificationRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value as NotificationType;
    dispatch(updateRequest({ qrCodeNotification: val }));
  };

  /**
   * Check if bulk is eligible for transfer
   */
  const isTransferEligible = (): boolean => {
    if (!transferEligible) {
      return false;
    }

    return upgradeDeviceRequest.notificationType === 'PULL' ? transferPullEnabled : true;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Summary */}
      <SectionTitleBlack>{t('pages.userDetails.upgradeDeviceDrawer.summary')}</SectionTitleBlack>
      <RowsContainer>
        <Row>
          <SectionLabel>{t('pages.userDetails.upgradeDeviceDrawer.userName')}</SectionLabel>
          <SectionValue>{`${user?.firstName} ${user?.lastName}`}</SectionValue>
        </Row>
        <Row>
          <SectionLabel>{t('pages.userDetails.upgradeDeviceDrawer.activationMethod')}</SectionLabel>
          <SectionValue>
            {t(`pages.userDetails.upgradeDeviceDrawer.${upgradeDeviceRequest.notificationType.toLocaleLowerCase()}`)}
          </SectionValue>
        </Row>
      </RowsContainer>

      {/* From section */}
      <SectionTitle>{t('pages.userDetails.upgradeDeviceDrawer.from')}</SectionTitle>
      <RowsContainer>
        <Row>
          <SectionLabelDisabled sx={{ width: '25%' }}>
            {t('pages.userDetails.upgradeDeviceDrawer.device')}
          </SectionLabelDisabled>
          <SectionValueDisabled
            sx={{ width: '75%', textAlign: 'end' }}
          >{`${deviceToUpgrade?.manufacturer} ${deviceToUpgrade?.model}`}</SectionValueDisabled>
        </Row>
        <Row>
          <SectionLabelDisabled sx={{ width: '25%' }}>
            {selectedSimType === 'PSIM'
              ? t('pages.userDetails.upgradeDeviceDrawer.iccId')
              : t('pages.userDetails.upgradeDeviceDrawer.eSimId')}
          </SectionLabelDisabled>
          <SectionValueDisabled sx={{ width: '75%' }}>{selectedSimId}</SectionValueDisabled>
        </Row>
      </RowsContainer>

      {/* To section */}
      <SectionTitleBlack>{t('pages.userDetails.upgradeDeviceDrawer.to')}</SectionTitleBlack>
      <RowsContainer>
        <Row>
          <SectionLabel sx={{ width: '25%' }}>{t('pages.userDetails.upgradeDeviceDrawer.device')}</SectionLabel>
          <SectionValue
            sx={{ width: '75%', textAlign: 'end' }}
          >{`${newDevice?.manufacturer} ${newDevice?.model}`}</SectionValue>
        </Row>
        <Row>
          <SectionLabel sx={{ width: '25%' }}>{t('pages.userDetails.upgradeDeviceDrawer.eSimId')}</SectionLabel>
          <SectionValue sx={{ width: '75%' }}>{newDevice?.simId}</SectionValue>
        </Row>
      </RowsContainer>

      {isTransferEligible() ? (
        <Box sx={{ marginTop: '1rem' }}>
          <RadioGroup
            aria-labelledby="source-device-access"
            name="source-device-access"
            value={upgradeDeviceRequest.transfer.toString()}
            onChange={handleTransferRadioChange}
          >
            <Radio value="true" label={t('pages.userDetails.upgradeDeviceDrawer.transferUserHasAccess')} />
            <Radio value="false" label={t('pages.userDetails.upgradeDeviceDrawer.transferNoAccess')} />
          </RadioGroup>
        </Box>
      ) : null}

      {/* Transfer warning */}
      {transferEligible && upgradeDeviceRequest.transfer ? (
        <WarningContainer>
          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '1rem' }}>
            <WarningIconTransparent />
            <Typography sx={{ fontWeight: '700' }}>
              {t('pages.userDetails.upgradeDeviceDrawer.transferWarningTitle')}
            </Typography>
          </Box>
          <Typography sx={{ padding: '1rem', paddingTop: 0 }}>
            <Trans
              i18nKey="pages.userDetails.upgradeDeviceDrawer.transferWarningMessage1"
              components={{ Bold: <strong /> }}
            />
          </Typography>
          <Typography sx={{ padding: '1rem', paddingTop: 0 }}>
            {t('pages.userDetails.upgradeDeviceDrawer.transferWarningMessage2')}
          </Typography>
        </WarningContainer>
      ) : (
        <Box
          width="100%"
          sx={{
            backgroundColor: (theme) => theme.palette.monochrome.monoChrome2,
            marginTop: '1rem',
            marginBottom: '1.5rem',
          }}
        >
          <Typography sx={{ padding: '1rem' }}>
            {t('pages.userDetails.upgradeDeviceDrawer.upgradeWarningMessage')}
          </Typography>
        </Box>
      )}

      {/* Notification */}
      <SectionTitle sx={{ paddingTop: '1rem' }}>
        {t('pages.userDetails.upgradeDeviceDrawer.notificationSettings')}
      </SectionTitle>
      <Box sx={{ paddingBottom: '1rem' }}>
        <Typography sx={{ paddingBottom: '1.5rem' }}>
          {upgradeDeviceRequest.notificationType === 'PUSH'
            ? t('pages.userDetails.upgradeDeviceDrawer.qrCodeQuestion_push')
            : t('pages.userDetails.upgradeDeviceDrawer.qrCodeQuestion')}
        </Typography>
        <RadioGroup
          aria-labelledby="qr-code-destination"
          name="qr-code-destination"
          value={upgradeDeviceRequest.qrCodeNotification}
          onChange={handleNotificationRadioChange}
        >
          {qrCodeNotificationSettings.map((ns: NotificationSetting) => (
            <Radio
              value={ns.type}
              key={ns.type}
              label={
                <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                  {t(`pages.userDetails.upgradeDeviceDrawer.${ns.type}`)}
                  <Typography sx={{ paddingLeft: '1rem', color: (theme) => theme.palette.text.disabled }}>
                    {ns.label}
                  </Typography>
                </Box>
              }
            />
          ))}
        </RadioGroup>
      </Box>
      <Divider
        sx={{
          borderColor: (theme) => theme.palette.monochrome.monoChrome3,
          marginBottom: '1.5rem',
        }}
      />

      <Box sx={{ marginBottom: '1rem' }}>
        <Checkbox
          checked={upgradeDeviceRequest.notifyAdmin}
          onChange={(e) => {
            dispatch(updateRequest({ notifyAdmin: e.currentTarget.checked }));
          }}
          label={t('pages.userDetails.upgradeDeviceDrawer.sendMeNotifications')}
        />
      </Box>

      {hasDistributionList ? (
        <Box sx={{ marginBottom: '2.5rem' }}>
          <Checkbox
            checked={upgradeDeviceRequest.notifyDistributionList}
            onChange={(e) => {
              dispatch(updateRequest({ notifyDistributionList: e.currentTarget.checked }));
            }}
            label={t('pages.userDetails.upgradeDeviceDrawer.sendDLNotifications')}
          />
        </Box>
      ) : null}

      <Box sx={{ marginTop: 'auto' }}>
        <ButtonGroup>
          <Button label={t('common.cancel')} color="alternative2Button" onClick={onClose} disabled={isLoading} />
          <Button
            label={t('common.send')}
            color="primaryButton"
            onClick={nextStep}
            disabled={isLoading || sessionStorage.getItem('impersonateId') !== null}
          />
        </ButtonGroup>
      </Box>
    </Box>
  );
}

export default UpgradeDeviceSummaryStep;
